import { useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

interface AppImageProps {
  src?: string | null;
  alt?: string;
  width?: number | string;
  height?: number | string;
  aspectRatio?: string; // e.g., '1/1', '16/9'
  sx?: Record<string, unknown>;
  objectFit?: 'cover' | 'contain' | 'fill';
  /** Where the image anchors when cropped. 'top' shows faces in character art. */
  objectPosition?: string;
  fallbackIcon?: React.ReactNode;
  draggable?: boolean;
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export function AppImage({
  src,
  alt = '',
  width,
  height,
  aspectRatio,
  sx,
  objectFit = 'cover',
  objectPosition = 'top center',
  fallbackIcon,
  draggable,
  onDragStart,
  onClick,
}: AppImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <Box
        draggable={draggable}
        onDragStart={onDragStart}
        sx={{
          width,
          height,
          aspectRatio,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          ...sx,
        }}
      >
        {fallbackIcon ?? (
          <BrokenImageIcon sx={{ color: 'grey.400', fontSize: 40 }} />
        )}
      </Box>
    );
  }

  return (
    <Box
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onClick}
      sx={{
        position: 'relative',
        width,
        height,
        aspectRatio,
        overflow: 'hidden',
        borderRadius: 1,
        cursor: onClick ? 'pointer' : undefined,
        ...sx,
      }}
    >
      {!loaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: 'absolute', inset: 0 }}
        />
      )}
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        sx={{
          width: '100%',
          height: '100%',
          objectFit,
          objectPosition,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.2s ease-in',
        }}
      />
    </Box>
  );
}
